import { useEffect, useMemo, useState, type FormEvent } from 'react';
import type {
  CreateProjectRequest, MergeInRequest, MergeProjectRequest, ProjectAliasDto, ProjectDetail, ProjectListResponse, ProjectLookupResponse, ProjectMergeResponse, ProjectRefDto,
  RenameProjectRequest, ShareProjectRequest,
} from '../../shared/api-types.js';
import { PROJECT_NAME_MAX, projectNameKey } from '../../shared/projects.js';
import { api, type ApiFailure } from '../api.js';
import { Button, ConfirmDialog, Dialog, FailureView, inlineFailureText, PeopleCombobox, ProjectChip, SelectField, TextField, useRequest, type ComboboxOption } from '../ui/index.js';
import { deleteProjectTexts, mergeDialogSentences, mergeInConsentText, projectApiPath, renameRemedyQuestion, shareActPath, shareDisclosureText } from './lib/projects.js';
import { OWN_PROJECTS_PATH } from './project-checkboxes.js';
import './pages.css';

// The project dialogs (design §7.4; 03-components §4): Dialog + Field forms, as every other form in the client — the
// client has no inline editing. Each sends one request, shows the server's own sentence on failure (a 409 names the
// holder; a 400 says why a name was refused) and hands its answer to the page, which toasts — with Undo where the
// operation is reversible (rename, merge, share, merge in) and after a confirmation where it is not for the other
// party (delete, §4.2). Share and Merge in take the full-screen mobile form: a combobox's list must sit beneath its
// input, never in a bottom sheet under the rising keyboard (§3.4).

function errorLine(failure: ApiFailure): string {
  return failure.correlationId && failure.status >= 500 ? `${inlineFailureText(failure)} Reference ${failure.correlationId}.` : inlineFailureText(failure);
}

// --- New project ------------------------------------------------------------------------------------------------------

export interface NewProjectDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (created: ProjectDetail) => void;
}

export function NewProjectDialog({ open, onClose, onCreated }: NewProjectDialogProps) {
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setName('');
      setError(null);
      setSaving(false);
    }
  }, [open]);

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    const trimmed = name.trim();
    if (trimmed === '') {
      setError('Give the project a name.');
      return;
    }
    setSaving(true);
    const result = await api.post<ProjectDetail>('/api/projects', { name: trimmed } satisfies CreateProjectRequest);
    setSaving(false);
    if (!result.ok) {
      setError(errorLine(result));
      return;
    }
    onCreated(result.data);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="New project"
      description="A client, a product, an initiative — whatever your meetings and to-dos belong to. Calis files into it from here on."
      size="sm"
      initialFocus="first"
      unsavedChanges={name.trim() !== '' && !saving}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving} disabledReason={saving ? 'Wait for the save to finish.' : undefined}>Cancel</Button>
          <Button variant="primary" type="submit" form="project-new-form" loading={saving}>Create</Button>
        </>
      }
    >
      <form id="project-new-form" onSubmit={(event) => void submit(event)} noValidate className="stack stack--3">
        <TextField
          label="Name"
          required
          value={name}
          onChange={(event) => {
            setName(event.target.value);
            if (error) setError(null);
          }}
          maxLength={PROJECT_NAME_MAX}
          autoComplete="off"
          spellCheck={false}
          placeholder="e.g. Alpine Logistics"
          error={error}
          disabled={saving}
        />
      </form>
    </Dialog>
  );
}

// --- Rename -------------------------------------------------------------------------------------------------------------

/** What a 409 on a rename resolves to once the holder of the key is looked up: a live project, or another project's former name. */
type RenameConflict =
  | { kind: 'live'; project: ProjectRefDto }
  | { kind: 'alias'; project: ProjectRefDto; alias: ProjectAliasDto | null }
  | { kind: 'other' };

export interface RenameProjectDialogProps {
  /** The project to rename; the dialog is open while one is given. */
  project: Pick<ProjectDetail, 'id' | 'name'> | null;
  onClose: () => void;
  /** The renamed project and the name it had, for the page's "Project renamed — Undo". */
  onRenamed: (renamed: ProjectDetail, previousName: string) => void;
  /** The remedy for a taken name: the page opens its Merge dialog against the holder. */
  onMergeInstead: (into: ProjectRefDto) => void;
}

export function RenameProjectDialog({ project, onClose, onRenamed, onMergeInstead }: RenameProjectDialogProps) {
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<RenameConflict | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (project) {
      setName(project.name);
      setError(null);
      setConflict(null);
      setSaving(false);
    }
  }, [project]);

  const dirty = project !== null && name.trim() !== '' && name.trim() !== project.name;

  /** Who holds the key the person typed — so the 409 offers the remedy rather than a dead end (design §7.4). */
  const resolveConflict = async (typed: string): Promise<RenameConflict> => {
    const lookup = await api.get<ProjectLookupResponse>(`/api/projects/lookup?name=${encodeURIComponent(typed)}`);
    if (!lookup.ok || lookup.data.project === null) return { kind: 'other' };
    if (lookup.data.via === 'live') return { kind: 'live', project: lookup.data.project };
    // A former name of another project: its alias row is on that project's detail, matched by key.
    const holder = await api.get<ProjectDetail>(projectApiPath(lookup.data.project.id));
    const key = projectNameKey(typed);
    const alias = holder.ok ? holder.data.aliases.find((row) => projectNameKey(row.name) === key) ?? null : null;
    return { kind: 'alias', project: lookup.data.project, alias };
  };

  const rename = async (typed: string): Promise<void> => {
    if (!project) return;
    setSaving(true);
    setError(null);
    setConflict(null);
    const body: RenameProjectRequest = { name: typed, expectedName: project.name };
    const result = await api.patch<ProjectDetail>(projectApiPath(project.id), body);
    if (!result.ok) {
      setError(errorLine(result));
      if (result.code === 'conflict') setConflict(await resolveConflict(typed));
      setSaving(false);
      return;
    }
    setSaving(false);
    onRenamed(result.data, project.name);
  };

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    const typed = name.trim();
    if (typed === '') {
      setError('Give the project a name.');
      return;
    }
    if (!dirty) {
      onClose();
      return;
    }
    await rename(typed);
  };

  /** "Forget that name": drop the other project's former name, then rename again. */
  const forgetAndRetry = async (alias: ProjectAliasDto): Promise<void> => {
    setSaving(true);
    const result = await api.delete<unknown>(projectApiPath(alias.id));
    if (!result.ok) {
      setSaving(false);
      setError(errorLine(result));
      return;
    }
    await rename(name.trim());
  };

  // Why the remedy asks and does not restate: the field's error line already carries the server's sentence (and keeps
  // the field marked invalid for assistive technology); saying the fact twice, a line apart, read as two faults.
  const remedy = (() => {
    if (!conflict) return null;
    if (conflict.kind === 'live') {
      return (
        <div className="stack stack--2 project-dialog__remedy" role="group" aria-label="What you can do instead">
          <p className="text-sm">
            {renameRemedyQuestion('live')} <ProjectChip project={conflict.project} link={false} />
          </p>
          <div className="cluster">
            <Button variant="secondary" onClick={() => onMergeInstead(conflict.project)} disabled={saving} disabledReason={saving ? 'Wait for the save to finish.' : undefined}>Merge into {conflict.project.name}</Button>
          </div>
        </div>
      );
    }
    if (conflict.kind === 'alias') {
      const { alias } = conflict;
      const absorbed = alias?.absorbed === true;
      return (
        <div className="stack stack--2 project-dialog__remedy" role="group" aria-label="What you can do instead">
          <p className="text-sm">
            {renameRemedyQuestion(absorbed ? 'absorbed' : 'alias')} <ProjectChip project={conflict.project} link={false} />
          </p>
          <div className="cluster">
            {alias && !absorbed ? <Button variant="secondary" onClick={() => void forgetAndRetry(alias)} loading={saving}>Forget that name</Button> : null}
            <Button variant="secondary" onClick={() => onMergeInstead(conflict.project)} disabled={saving} disabledReason={saving ? 'Wait for the save to finish.' : undefined}>Merge into {conflict.project.name}</Button>
          </div>
        </div>
      );
    }
    return null;
  })();

  return (
    <Dialog
      open={project !== null}
      onClose={onClose}
      title="Rename project"
      size="md"
      initialFocus="first"
      unsavedChanges={dirty && !saving}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving} disabledReason={saving ? 'Wait for the save to finish.' : undefined}>Cancel</Button>
          <Button variant="primary" type="submit" form="project-rename-form" loading={saving} disabled={!dirty} disabledReason={dirty ? undefined : 'No changes to save.'}>Save</Button>
        </>
      }
    >
      <form id="project-rename-form" onSubmit={(event) => void submit(event)} noValidate className="stack stack--3">
        <TextField
          label="Name"
          required
          value={name}
          onChange={(event) => {
            setName(event.target.value);
            if (error) setError(null);
            if (conflict) setConflict(null);
          }}
          maxLength={PROJECT_NAME_MAX}
          autoComplete="off"
          spellCheck={false}
          error={error}
          hint="The old name stays as a former name, so anything that used it still finds this project."
          disabled={saving}
        />
        {remedy}
      </form>
    </Dialog>
  );
}

// --- Merge --------------------------------------------------------------------------------------------------------------

export interface MergeProjectDialogProps {
  /** The project to merge away (B), with its counts and shares; the dialog is open while one is given. */
  project: ProjectDetail | null;
  /** A survivor chosen before opening (the Rename dialog's "Merge into X"). */
  initialInto?: ProjectRefDto | null;
  onClose: () => void;
  onMerged: (response: ProjectMergeResponse, absorbed: ProjectDetail) => void;
}

export function MergeProjectDialog({ project, initialInto = null, onClose, onMerged }: MergeProjectDialogProps) {
  const open = project !== null;
  const [into, setInto] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // Why the person's own live projects, loaded here: the choice is among them alone (both must be own and live), and a
  // page that opened this from a list row has only that row.
  const own = useRequest<ProjectListResponse>(
    (signal) => (open ? api.get<ProjectListResponse>(OWN_PROJECTS_PATH, { signal }) : new Promise<never>(() => {})),
    [open],
  );
  // The survivor's own shares tell which recipients already see it, so the duplicate-share sentence is truthful (S1).
  const target = useRequest<ProjectDetail>(
    (signal) => (open && into !== '' ? api.get<ProjectDetail>(projectApiPath(into), { signal }) : new Promise<never>(() => {})),
    [open, into],
  );

  useEffect(() => {
    if (project) {
      setInto(initialInto?.id ?? '');
      setError(null);
      setSaving(false);
    }
  }, [project, initialInto]);

  const options = useMemo(
    () => (own.data?.items ?? []).filter((item) => item.id !== project?.id).map((item) => ({ value: item.id, label: item.name })),
    [own.data, project?.id],
  );
  const chosen = options.find((option) => option.value === into) ?? null;
  const sentences = project && chosen
    ? mergeDialogSentences({ absorbed: project, into: { name: chosen.label, shares: target.data?.id === into ? target.data.shares : null } })
    : [];

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (!project) return;
    if (into === '') {
      setError('Choose the project to merge into.');
      return;
    }
    setSaving(true);
    const result = await api.post<ProjectMergeResponse>(`${projectApiPath(project.id)}/merge`, { into } satisfies MergeProjectRequest);
    setSaving(false);
    if (!result.ok) {
      setError(errorLine(result));
      return;
    }
    onMerged(result.data, project);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={project ? `Merge ${project.name} into…` : 'Merge project'}
      size="md"
      initialFocus="first"
      unsavedChanges={into !== '' && into !== (initialInto?.id ?? '') && !saving}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving} disabledReason={saving ? 'Wait for the save to finish.' : undefined}>Cancel</Button>
          <Button variant="primary" type="submit" form="project-merge-form" loading={saving} disabled={into === ''} disabledReason={into === '' ? 'Choose the project to merge into first.' : undefined}>Merge</Button>
        </>
      }
    >
      <form id="project-merge-form" onSubmit={(event) => void submit(event)} noValidate className="stack stack--3">
        {own.status === 'error' && own.error ? (
          // Why FailureView (ruling Q11): a refusal reads as access restricted with its next step, never the envelope's words.
          <FailureView failure={own.error} what="your projects" onRetry={own.reload} compact />
        ) : options.length === 0 && own.status === 'ready' ? (
          <p className="text-sm secondary">You have no other project to merge this one into.</p>
        ) : (
          <SelectField
            // Why a noun (05-content §3: "a picker's label stays a noun"; the owner's ruling Q10 of escalation
            // 20261005-200526; Phase 1c R15): the field names the project chosen, the dialog's button the act.
            label="Project to merge into"
            required
            value={into}
            onChange={(event) => {
              setInto(event.target.value);
              if (error) setError(null);
            }}
            options={options}
            placeholderOption={own.status === 'loading' ? 'Loading your projects…' : 'Choose a project'}
            error={error}
            disabled={saving || own.status !== 'ready'}
          />
        )}
        {sentences.length > 0 ? (
          <div className="dialog__text stack stack--2">
            {sentences.map((sentence) => <p key={sentence}>{sentence}</p>)}
          </div>
        ) : null}
      </form>
    </Dialog>
  );
}

// --- Share --------------------------------------------------------------------------------------------------------------

export interface ShareProjectDialogProps {
  project: Pick<ProjectDetail, 'id' | 'name'> | null;
  onClose: () => void;
  /** The project as the server answers it (its `shares` now hold the new one) and the person chosen. */
  onShared: (detail: ProjectDetail, recipient: ComboboxOption) => void;
  /**
   * Executive intelligence is on (`me.integrations.executive`; ADR 0019): the disclosure then says the project's name may
   * appear in the executives' report (design §2.14 item 9). A prop, not `useMe()`, so the dialog renders on its own.
   */
  executiveOn?: boolean;
}

export function ShareProjectDialog({ project, onClose, onShared, executiveOn = false }: ShareProjectDialogProps) {
  const [recipient, setRecipient] = useState<ComboboxOption | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (project) {
      setRecipient(null);
      setError(null);
      setSaving(false);
    }
  }, [project]);

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (!project) return;
    if (!recipient) {
      setError('Choose who to share it with.');
      return;
    }
    setSaving(true);
    const result = await api.post<ProjectDetail>(`${projectApiPath(project.id)}/shares`, { recipientId: recipient.id } satisfies ShareProjectRequest);
    setSaving(false);
    if (!result.ok) {
      setError(errorLine(result));
      return;
    }
    onShared(result.data, recipient);
  };

  return (
    <Dialog
      open={project !== null}
      onClose={onClose}
      title={project ? `Share ${project.name}` : 'Share project'}
      size="md"
      mobile="fullscreen"
      initialFocus="first"
      unsavedChanges={recipient !== null && !saving}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving} disabledReason={saving ? 'Wait for the save to finish.' : undefined}>Cancel</Button>
          <Button variant="primary" type="submit" form="project-share-form" loading={saving} disabled={recipient === null} disabledReason={recipient === null ? 'Choose who to share with first.' : undefined}>Share</Button>
        </>
      }
    >
      <form id="project-share-form" onSubmit={(event) => void submit(event)} noValidate className="stack stack--3">
        <PeopleCombobox
          // Why a noun: §3's picker rule and the owner's ruling Q10 (Phase 1c R15), as the merge picker above.
          label="Person to share with"
          value={recipient}
          onChange={(next) => {
            setRecipient(next);
            if (error) setError(null);
          }}
          required
          // An example, never an instruction (05-content §3): the label says what the field is for.
          placeholder="e.g. Ada Lovelace"
          error={error}
          disabled={saving}
        />
        <p className="dialog__text project-share__disclosure">{shareDisclosureText(recipient?.label ?? null, executiveOn)}</p>
      </form>
    </Dialog>
  );
}

// --- Merge in (the recipient's side) -------------------------------------------------------------------------------------

export interface MergeInDialogProps {
  /** The shared project as the recipient sees it; the dialog is open while one is given. */
  share: { id: string; projectName: string; sharedBy: { id: string; displayName: string } } | null;
  onClose: () => void;
  /** The recipient's own project the share is now grouped under, as the server answers it. */
  onMergedIn: (into: ProjectDetail) => void;
}

const NEW_PROJECT_OPTION = '__new__';

export function MergeInDialog({ share, onClose, onMergedIn }: MergeInDialogProps) {
  const open = share !== null;
  const [into, setInto] = useState('');
  const [newName, setNewName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const own = useRequest<ProjectListResponse>(
    (signal) => (open ? api.get<ProjectListResponse>(OWN_PROJECTS_PATH, { signal }) : new Promise<never>(() => {})),
    [open],
  );

  useEffect(() => {
    if (share) {
      setInto('');
      setNewName('');
      setError(null);
      setNameError(null);
      setSaving(false);
    }
  }, [share]);

  const options = useMemo(() => [
    ...(own.data?.items ?? []).map((item) => ({ value: item.id, label: item.name })),
    { value: NEW_PROJECT_OPTION, label: 'New project…' },
  ], [own.data]);
  const creating = into === NEW_PROJECT_OPTION;
  const ready = into !== '' && (!creating || newName.trim() !== '');

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (!share) return;
    if (into === '') {
      setError('Choose one of your projects, or name a new one.');
      return;
    }
    setSaving(true);
    setError(null);
    setNameError(null);
    let targetId = into;
    if (creating) {
      const created = await api.post<ProjectDetail>('/api/projects', { name: newName.trim() } satisfies CreateProjectRequest);
      if (!created.ok) {
        setSaving(false);
        setNameError(errorLine(created));
        return;
      }
      targetId = created.data.id;
      // Why the new project is taken into the select at once: the create and the merge-in are two requests, and should
      // the second fail (the share ended meanwhile, a merge-in from another tab) a retry must group under the project
      // just made rather than create it again and meet its own 409 "You already have a project called X".
      own.setData((current) => (current ? { ...current, items: [...current.items, created.data], total: current.total + 1, matching: current.matching + 1 } : current));
      setInto(created.data.id);
      setNewName('');
    }
    const result = await api.post<ProjectDetail>(shareActPath(share.id, 'merge-in'), { into: targetId } satisfies MergeInRequest);
    setSaving(false);
    if (!result.ok) {
      setError(errorLine(result));
      return;
    }
    onMergedIn(result.data);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={share ? `Merge ${share.projectName} into one of mine` : 'Merge in'}
      size="md"
      mobile="fullscreen"
      initialFocus="first"
      unsavedChanges={(into !== '' || newName.trim() !== '') && !saving}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving} disabledReason={saving ? 'Wait for the save to finish.' : undefined}>Cancel</Button>
          <Button
            variant="primary"
            type="submit"
            form="project-merge-in-form"
            loading={saving}
            disabled={!ready}
            disabledReason={ready ? undefined : into === '' ? 'Choose one of your projects first.' : 'Name the new project first.'}
          >
            Merge in
          </Button>
        </>
      }
    >
      <form id="project-merge-in-form" onSubmit={(event) => void submit(event)} noValidate className="stack stack--3">
        {own.status === 'error' && own.error ? (
          // Why FailureView (ruling Q11): a refusal reads as access restricted with its next step, never the envelope's words.
          <FailureView failure={own.error} what="your projects" onRetry={own.reload} compact />
        ) : (
          <SelectField
            // Why a noun: §3's picker rule and the owner's ruling Q10 (Phase 1c R15) — the project the shared one is grouped under.
            label="Parent project"
            required
            value={into}
            onChange={(event) => {
              setInto(event.target.value);
              if (error) setError(null);
            }}
            options={options}
            placeholderOption={own.status === 'loading' ? 'Loading your projects…' : 'Choose a project'}
            error={error}
            disabled={saving || own.status !== 'ready'}
          />
        )}
        {creating ? (
          <TextField
            label="New project"
            required
            value={newName}
            onChange={(event) => {
              setNewName(event.target.value);
              if (nameError) setNameError(null);
            }}
            maxLength={PROJECT_NAME_MAX}
            autoComplete="off"
            spellCheck={false}
            placeholder="e.g. Alpine Logistics"
            error={nameError}
            disabled={saving}
          />
        ) : null}
        {share ? <p className="dialog__text project-share__disclosure">{mergeInConsentText(share.sharedBy.displayName)}</p> : null}
      </form>
    </Dialog>
  );
}

// --- Delete -------------------------------------------------------------------------------------------------------------

export interface DeleteProjectDialogProps {
  project: Pick<ProjectDetail, 'id' | 'name'> | null;
  onClose: () => void;
  onDeleted: (project: Pick<ProjectDetail, 'id' | 'name'>) => void;
}

export function DeleteProjectDialog({ project, onClose, onDeleted }: DeleteProjectDialogProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (project) {
      setBusy(false);
      setError(null);
    }
  }, [project]);
  const texts = deleteProjectTexts(project?.name ?? '');
  return (
    <ConfirmDialog
      open={project !== null}
      title={texts.title}
      confirmLabel={texts.confirm}
      danger
      busy={busy}
      typeToConfirm={project?.name}
      error={error}
      onCancel={onClose}
      onConfirm={async () => {
        if (!project) return;
        setBusy(true);
        const result = await api.delete<unknown>(projectApiPath(project.id));
        setBusy(false);
        if (!result.ok) {
          setError(errorLine(result));
          return;
        }
        onDeleted(project);
      }}
    >
      <p>{texts.body}</p>
    </ConfirmDialog>
  );
}
